<template>
  <div class="duration-hm-input formulate-input">
    <label v-if="label" :for="id" class="formulate-input-label">{{ label }}</label>
    <div class="duration-hm-container formulate-input-element" :class="{ 'duration-hm-container--invalid': state === false }">
      <div class="time-segment">
        <button
          type="button"
          class="time-btn time-btn-up"
          :disabled="disabled || hours >= maxHours"
          @click="incrementHours"
        >
          <feather-icon icon="ChevronUpIcon" size="14" />
        </button>
        <input
          :id="`${id}-hours`"
          type="text"
          :value="hoursDisplay"
          @input="handleHoursInput"
          @blur="handleHoursBlur"
          @focus="handleHoursFocus"
          :disabled="disabled"
          class="time-input"
          maxlength="2"
        />
        <button
          type="button"
          class="time-btn time-btn-down"
          :disabled="disabled || hours <= 0"
          @click="decrementHours"
        >
          <feather-icon icon="ChevronDownIcon" size="14" />
        </button>
      </div>
      <span class="time-separator">:</span>
      <div class="time-segment">
        <button
          type="button"
          class="time-btn time-btn-up"
          :disabled="disabled || minutes >= 59"
          @click="incrementMinutes"
        >
          <feather-icon icon="ChevronUpIcon" size="14" />
        </button>
        <input
          :id="`${id}-minutes`"
          type="text"
          :value="minutesDisplay"
          @input="handleMinutesInput"
          @blur="handleMinutesBlur"
          @focus="handleMinutesFocus"
          :disabled="disabled"
          class="time-input"
          maxlength="2"
        />
        <button
          type="button"
          class="time-btn time-btn-down"
          :disabled="disabled || minutes <= 0"
          @click="decrementMinutes"
        >
          <feather-icon icon="ChevronDownIcon" size="14" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DurationHmInput",
  props: {
    value: {
      type: [String, Object],
      default: null,
    },
    label: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxHours: {
      type: Number,
      default: 999,
    },
    state: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      hours: 0,
      minutes: 0,
      hoursEditing: false,
      minutesEditing: false,
    };
  },
  computed: {
    id() {
      return `duration-hm-${this._uid}`;
    },
    hoursDisplay() {
      if (this.hoursEditing) {
        return this.hours === 0 ? "" : String(this.hours);
      }
      return String(this.hours).padStart(2, "0");
    },
    minutesDisplay() {
      if (this.minutesEditing) {
        return this.minutes === 0 ? "" : String(this.minutes);
      }
      return String(this.minutes).padStart(2, "0");
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.parseValue(newVal);
      },
    },
  },
  methods: {
    parseValue(val) {
      if (val === null || val === undefined) {
        this.hours = 0;
        this.minutes = 0;
        return;
      }
      if (typeof val === "object") {
        this.hours = Math.max(0, Number(val.hours) || 0);
        this.minutes = Math.max(0, Math.min(59, Number(val.minutes) || 0));
        return;
      }
      if (typeof val === "string") {
        const parts = val.split(":");
        if (parts.length >= 2) {
          this.hours = Math.max(0, Number(parts[0]) || 0);
          this.minutes = Math.max(0, Math.min(59, Number(parts[1]) || 0));
          return;
        }
      }
      this.hours = 0;
      this.minutes = 0;
    },
    normalizeTime() {
      if (this.minutes > 59) {
        const extraHours = Math.floor(this.minutes / 60);
        this.hours += extraHours;
        this.minutes = this.minutes % 60;
      }
      if (this.minutes < 0) this.minutes = 0;
      if (this.hours < 0) this.hours = 0;
      if (this.hours > this.maxHours) {
        this.hours = this.maxHours;
        this.minutes = 0;
      }
    },
    emitValue() {
      this.normalizeTime();
      const value = { hours: this.hours, minutes: this.minutes };
      this.$emit("input", value);
    },
    incrementHours() {
      if (this.hours < this.maxHours) {
        this.hours++;
        this.emitValue();
      }
    },
    decrementHours() {
      if (this.hours > 0) {
        this.hours--;
        this.emitValue();
      }
    },
    incrementMinutes() {
      if (this.minutes < 59) {
        this.minutes++;
        this.emitValue();
      } else if (this.hours < this.maxHours) {
        this.hours++;
        this.minutes = 0;
        this.emitValue();
      }
    },
    decrementMinutes() {
      if (this.minutes > 0) {
        this.minutes--;
        this.emitValue();
      } else if (this.hours > 0) {
        this.hours--;
        this.minutes = 59;
        this.emitValue();
      }
    },
    handleHoursFocus() {
      this.hoursEditing = true;
    },
    handleMinutesFocus() {
      this.minutesEditing = true;
    },
    handleHoursInput(event) {
      const val = event.target.value.replace(/\D/g, "");
      this.hours = val === "" ? 0 : Math.min(Number(val), this.maxHours);
      this.emitValue();
    },
    handleHoursBlur() {
      this.hoursEditing = false;
      this.normalizeTime();
      this.emitValue();
      this.$forceUpdate();
    },
    handleMinutesInput(event) {
      const val = event.target.value.replace(/\D/g, "");
      this.minutes = val === "" ? 0 : Math.min(Number(val), 59);
      this.emitValue();
    },
    handleMinutesBlur() {
      this.minutesEditing = false;
      this.normalizeTime();
      this.emitValue();
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped>
.duration-hm-input {
  width: 100%;
  margin-bottom: 12px;
}

.formulate-input-label {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
  color: #5e5873;
}

.duration-hm-container {
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  padding: 4px 8px;
  background: #fff;
  transition: all 0.2s;
}

.duration-hm-container:focus-within {
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.1);
}

.duration-hm-container--invalid {
  border-color: #ea5455;
}

.duration-hm-container--invalid:focus-within {
  border-color: #ea5455;
  box-shadow: 0 0 0 2px rgba(234, 84, 85, 0.1);
}

.time-segment {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.time-input {
  width: 42px;
  text-align: center;
  border: none;
  background: transparent;
  padding: 4px 2px;
  font-size: 16px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  outline: none;
}

.time-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.time-btn {
  width: 24px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s;
  color: #6e6b7b;
}

.time-btn:hover:not(:disabled) {
  background-color: #f8f8f8;
  color: #7367f0;
}

.time-btn:active:not(:disabled) {
  background-color: #e8e6f0;
}

.time-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.time-separator {
  font-size: 18px;
  font-weight: 600;
  color: #6e6b7b;
  margin: 0 4px;
  line-height: 1;
  user-select: none;
}
</style>
